import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { defaultServiceApi, ApiError } from '../api';
import type { DefaultService } from '../types/admin';
import type { Service } from './servicesStore';
import type { ServiceRequest } from '../types/provider';

// Re-export UI types for convenience
export type { Service, ServiceVariant } from './servicesStore';

// Map API response → UI model
const mapApiService = (apiService: DefaultService): Service => ({
	id: String(apiService.id),
	name: apiService.serviceName,
	description: apiService.description,
	status: apiService.status,
	// Global templates have no add-on flag; the editor hides the checkbox here.
	addOn: false,
	variants: apiService.pricingCombinations.map((combo, idx) => ({
		id: `${apiService.id}-${idx}`,
		size: combo.size,
		length: combo.length,
		duration: combo.durationMinutes,
		price: combo.price,
	})),
});

// Map UI model → API request body
const toServiceRequest = (service: Service): ServiceRequest => ({
	serviceName: service.name,
	description: service.description,
	pricingCombinations: service.variants.map(v => ({
		size: v.size,
		length: v.length,
		durationMinutes: v.duration,
		price: v.price,
	})),
	status: service.status,
});

interface DefaultServicesState {
	services: Service[];
	isLoading: boolean;
	error: string | null;
	fetchServices: () => Promise<void>;
	addService: (service: Service) => Promise<void>;
	updateService: (id: string, service: Service) => Promise<void>;
	deleteService: (id: string) => Promise<void>;
	clearServices: () => void;
}

const useDefaultServicesStore = create<DefaultServicesState>()(
	devtools(
		set => ({
			services: [],
			isLoading: false,
			error: null,

			fetchServices: async () => {
				set({ isLoading: true, error: null });

				try {
					const apiServices = await defaultServiceApi.getAll();
					set({
						services: apiServices.map(mapApiService),
						isLoading: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						error: apiError.message || 'Failed to load default services',
						isLoading: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Default services fetch error:', apiError);
					}
				}
			},

			addService: async (service: Service) => {
				const created = await defaultServiceApi.create(toServiceRequest(service));
				set(state => ({
					services: [...state.services, mapApiService(created)],
				}));
			},

			updateService: async (id: string, service: Service) => {
				const updated = await defaultServiceApi.update(
					Number(id),
					toServiceRequest(service)
				);
				set(state => ({
					services: state.services.map(s => (s.id === id ? mapApiService(updated) : s)),
				}));
			},

			deleteService: async (id: string) => {
				await defaultServiceApi.delete(Number(id));
				set(state => ({
					services: state.services.filter(s => s.id !== id),
				}));
			},

			clearServices: () => {
				set({ services: [], isLoading: false, error: null });
			},
		}),
		{ name: 'default-services-store' }
	)
);

export default useDefaultServicesStore;
